import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Info, X, XCircle } from "lucide-react";
import clsx from "clsx";
import { useFeedback } from "@/lib/feedback";

/** Toasts (bottom-right) + the confirmation dialog. Mounted once at the app root. */
export default function FeedbackLayer() {
  const toasts = useFeedback((s) => s.toasts);
  const dismiss = useFeedback((s) => s.dismiss);
  const req = useFeedback((s) => s.confirmRequest);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!req) return;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") req.resolve(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [req]);

  return createPortal(
    <>
      <div
        className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 w-[min(380px,calc(100vw-2.5rem))]"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const Icon = t.tone === "ok" ? CheckCircle2 : t.tone === "err" ? XCircle : Info;
          return (
            <div
              key={t.id}
              role="status"
              className="panel animate-fade-up flex items-start gap-2.5 px-4 py-3 text-md"
            >
              <Icon
                className={clsx(
                  "w-4 h-4 mt-0.5 shrink-0",
                  t.tone === "ok" && "text-accent-green",
                  t.tone === "err" && "text-accent-pink",
                  t.tone === "info" && "text-accent-azure",
                )}
              />
              <span className="flex-1 min-w-0 whitespace-pre-line">{t.message}</span>
              <button
                className="text-mut-500 hover:text-mut-100"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {req && (
        <div className="fixed inset-0 z-[90] grid place-items-center p-4">
          <div className="overlay-backdrop" onClick={() => req.resolve(false)} />
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            className="panel relative w-[min(420px,100%)] p-5 animate-fade-up"
          >
            <h2 id="confirm-title" className="text-base font-semibold">
              {req.title}
            </h2>
            {req.body && <p className="mt-1.5 text-md text-mut-300">{req.body}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button className="btn" onClick={() => req.resolve(false)}>
                Cancel
              </button>
              <button
                ref={confirmRef}
                className={req.danger ? "btn btn-danger" : "btn btn-primary"}
                onClick={() => req.resolve(true)}
              >
                {req.confirmLabel ?? "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
